// WhyUs.jsx - The argument, made once, without superlatives.
//
// Every claim on this page is something a person can check on this site in under
// a minute. That constraint is the whole point: it is what separates this from
// the "highest payout guaranteed" pages everybody else runs.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { PageHead, CtaBand, Quote, OfferReceipt, MobileCta, Figure } from "./parts.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { VideoRecord, Loupe, TruckRoute, HallmarkZoom } from "../ui/illustrations.jsx";
import { ArrowRight, Video, Scale, Shield, Note } from "../ui/icons.jsx";

const PILLARS = [
  {
    icon: Video,
    art: VideoRecord,
    title: "We film every opening",
    body: "Not on request. Not for large lots. Every single package, recorded unbroken from the sealed envelope to the scale, and posted to your tracking page the same day.",
    check: "You can watch yours before you answer the offer.",
  },
  {
    icon: Scale,
    art: Loupe,
    title: "We publish every weight",
    body: "Gross weight, stone deduction, metal content in grams, and the test method for each piece. Not a lump sum. Not a range. The actual arithmetic.",
    check: "Which means you can compare our offer to anyone else's on the same numbers.",
  },
  {
    icon: Shield,
    art: TruckRoute,
    title: "Declining is genuinely free",
    body: "Insured return shipping at our cost, packed exactly as it arrived, usually the next business day. No restocking fee, no return postage, and nobody calls to talk you round.",
    check: "It is in our terms, not just on a marketing page.",
  },
  {
    icon: Note,
    art: HallmarkZoom,
    title: "Everything is written down",
    body: "Every status change, every message, every scan, every photo, on one page with a timestamp. If we say we texted you on Tuesday, the text is there.",
    check: "Your order page is the same log we look at internally.",
  },
];

const QUOTES = [
  { text: "The itemised sheet is what did it. Two other places quoted me one number for a whole bag and would not explain it.", name: "Denise O.", place: "Tempe, Arizona" },
  { text: "I watched the video before I answered. Seeing my mother's ring on the scale was worth more to me than the extra twenty dollars somewhere else.", name: "Hal B.", place: "Duluth, Minnesota" },
];

export default function WhyUs() {
  return (
    <>
      <Seo
        title="Why sell to us"
        path="/why-us"
        description="Every package opened on camera, every weight and purity test published, and genuinely free insured returns if you decline. Claims you can check in under a minute."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Why us", to: "/why-us" }])} />

      <PageHead
        eyebrow="Why us"
        title="Four claims, all of them checkable."
        lede="This industry runs on superlatives, and they are worth nothing. So here is only what we can show you evidence of, in the time it takes to read this page."
      />

      <div className="shell band-tight">
        <StaggerBoxed
          as="ol"
          step={110}
          from="up"
          className="grid gap-px bg-rule border border-rule md:grid-cols-2"
          itemClassName="bg-paper h-full"
        >
          {PILLARS.map((pillar, index) => {
            const Glyph = pillar.icon;
            const Drawing = pillar.art;
            return (
              <li key={pillar.title} className="p-6 sm:p-8 md:p-9 h-full flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="numeral text-[2.2rem]">{String(index + 1).padStart(2, "0")}</span>
                    <Glyph size={20} className="text-bronze" />
                  </div>
                  <Drawing className="w-24 sm:w-28 shrink-0 -mt-2 -mr-1 opacity-90" />
                </div>
                <h2 className="display display-md text-ink">{pillar.title}</h2>
                <p className="mt-3 text-[15px] sm:text-[15.5px] leading-relaxed text-ink-soft flex-1">{pillar.body}</p>
                <p className="mt-5 pt-4 border-t border-rule-soft text-[13.5px] font-semibold text-bronze-dark">
                  {pillar.check}
                </p>
              </li>
            );
          })}
        </StaggerBoxed>
      </div>

      <section className="border-y border-rule bg-surface/40 band clip-x">
        <div className="shell grid gap-9 lg:grid-cols-2 lg:gap-16 lg:items-center">
          <Reveal>
            <p className="eyebrow mb-3">What an offer looks like</p>
            <h2 className="display display-lg text-ink">This is the whole document.</h2>
            <p className="lede mt-4 sm:mt-5 measure text-muted">
              Not a summary of it. Every piece we weighed, what it weighed, what came off for stones,
              how much actual metal was in it, and what we are paying for that piece.
            </p>
            <p className="hint mt-5 leading-relaxed measure">
              We do not publish a percentage, because a single percentage would be misleading: the rate
              depends on the metal, the purity and the size of the lot. What we publish instead is every
              input, so you can work out the rate yourself and compare it to anyone.
            </p>
            <Link to="/get-a-kit" className="btn btn-primary mt-7">Get a free kit <ArrowRight size={16} /></Link>
          </Reveal>

          <Reveal from="right" delay={130}>
            <OfferReceipt className="lg:ml-auto max-w-md w-full" />
          </Reveal>
        </div>
      </section>

      <section className="shell band">
        <StaggerBoxed as="div" step={130} className="grid gap-8 md:grid-cols-2 md:gap-16">
          {QUOTES.map((quote) => <Quote key={quote.name} {...quote} />)}
        </StaggerBoxed>
      </section>

      <CtaBand
        title="Check it yourself."
        body="Ask for a kit, send one piece, and see the whole process end to end before you commit anything you care about."
      />
      <MobileCta />
    </>
  );
}
